import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Settings, Plus, Pencil, Trash2, Loader2, AlertTriangle, Briefcase, Building2, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";
import AccessRestricted from "../components/AccessRestricted";
import { useData } from "../context/DataContext";
import { toast } from "sonner";
import { JobRoleLocation } from "../types";

interface AffectedJobRole {
  id: string;
  title: string;
  department: string;
}

const JobRoleSettings: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const { jobTitles, fetchJobTitles, addJobTitle, updateJobTitle, deleteJobTitle, jobRoles, jobRoleLocations, fetchJobRoleLocations, addJobRoleLocation, updateJobRoleLocation, deleteJobRoleLocation, jobGrades, fetchJobGrades, addJobGrade, updateJobGrade, deleteJobGrade } = useData();

  if (!ability.can(ACTIONS.READ, RESOURCES.JOB_ROLE_SETTINGS)) {
    return <AccessRestricted />;
  }

  const [activeTab, setActiveTab] = useState("job-titles");
  const [loading, setLoading] = useState(false);
  const [newTitleName, setNewTitleName] = useState("");
  const [editingTitle, setEditingTitle] = useState<{ id: string; name: string } | null>(null);
  const [editName, setEditName] = useState("");

  // Delete flow for titles
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [warnModalOpen, setWarnModalOpen] = useState(false);
  const [titleToDelete, setTitleToDelete] = useState<{ id: string; name: string } | null>(null);
  const [affectedRoles, setAffectedRoles] = useState<AffectedJobRole[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  // Locations state
  const [newLocationName, setNewLocationName] = useState("");
  const [editingLocation, setEditingLocation] = useState<{ id: string; name: string } | null>(null);
  const [editLocationName, setEditLocationName] = useState("");
  const [locationSearchTerm, setLocationSearchTerm] = useState("");
  const [locationDeleteModalOpen, setLocationDeleteModalOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<JobRoleLocation | null>(null);
  const [locationDeleteLoading, setLocationDeleteLoading] = useState(false);

  // Grades state
  const [newGradeName, setNewGradeName] = useState("");
  const [newGradeSalaryMin, setNewGradeSalaryMin] = useState("");
  const [newGradeSalaryMax, setNewGradeSalaryMax] = useState("");
  const [editingGrade, setEditingGrade] = useState<{ id: string; name: string; salaryRangeMin?: number; salaryRangeMax?: number } | null>(null);
  const [editGradeName, setEditGradeName] = useState("");
  const [editGradeSalaryMin, setEditGradeSalaryMin] = useState("");
  const [editGradeSalaryMax, setEditGradeSalaryMax] = useState("");
  const [gradeSearchTerm, setGradeSearchTerm] = useState("");
  const [gradeDeleteModalOpen, setGradeDeleteModalOpen] = useState(false);
  const [gradeToDelete, setGradeToDelete] = useState<{ id: string; name: string } | null>(null);
  const [gradeDeleteLoading, setGradeDeleteLoading] = useState(false);

  const activeTitles = jobTitles
    .filter(jt => !jt.deleted)
    .filter(jt => jt.name.toLowerCase().includes(searchTerm.toLowerCase()));

  useEffect(() => {
    if (jobTitles.length === 0) {
      fetchJobTitles();
    }
    if (jobRoleLocations.length === 0) {
      fetchJobRoleLocations();
    }
    if (jobGrades.length === 0) {
      fetchJobGrades();
    }
  }, []);

  const handleAdd = async () => {
    if (!newTitleName.trim()) {
      toast.error("Job title name is required");
      return;
    }
    try {
      setLoading(true);
      await addJobTitle(newTitleName.trim());
      setNewTitleName("");
    } catch (error) {
      // Error is already toasted
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (title: { id: string; name: string }) => {
    setEditingTitle(title);
    setEditName(title.name);
  };

  const cancelEdit = () => {
    setEditingTitle(null);
    setEditName("");
  };

  const handleUpdate = async () => {
    if (!editingTitle) return;
    if (!editName.trim()) {
      toast.error("Job title name is required");
      return;
    }
    try {
      setLoading(true);
      await updateJobTitle(editingTitle.id, editName.trim());
      setEditingTitle(null);
      setEditName("");
    } catch (error) {
      // Error is already toasted
    } finally {
      setLoading(false);
    }
  };

  const openDeleteFlow = (title: { id: string; name: string }) => {
    // Check locally which job roles use this title
    const affected = jobRoles
      .filter(jr => jr.title === title.name && !jr.archived)
      .map(jr => ({
        id: jr.id,
        title: jr.title,
        department: jr.department?.departmentName || "Unknown"
      }));

    setTitleToDelete(title);
    setAffectedRoles(affected);

    if (affected.length > 0) {
      setWarnModalOpen(true);
    } else {
      setDeleteModalOpen(true);
    }
  };

  const proceedToDelete = () => {
    setWarnModalOpen(false);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!titleToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteJobTitle(titleToDelete.id);
      setDeleteModalOpen(false);
      setTitleToDelete(null);
      setAffectedRoles([]);
    } catch (error) {
      // Error is already toasted
    } finally {
      setDeleteLoading(false);
    }
  };

  // Location handlers
  const handleAddLocation = async () => {
    if (!newLocationName.trim()) {
      toast.error("Location name is required");
      return;
    }
    try {
      setLoading(true);
      await addJobRoleLocation(newLocationName.trim());
      setNewLocationName("");
    } catch (error) {
      // Error is already toasted
    } finally {
      setLoading(false);
    }
  };

  const startEditLocation = (location: { id: string; name: string }) => {
    setEditingLocation(location);
    setEditLocationName(location.name);
  };

  const cancelEditLocation = () => {
    setEditingLocation(null);
    setEditLocationName("");
  };

  const handleUpdateLocation = async () => {
    if (!editingLocation) return;
    if (!editLocationName.trim()) {
      toast.error("Location name is required");
      return;
    }
    try {
      setLoading(true);
      await updateJobRoleLocation(editingLocation.id, editLocationName.trim());
      setEditingLocation(null);
      setEditLocationName("");
    } catch (error) {
      // Error is already toasted
    } finally {
      setLoading(false);
    }
  };

  const openLocationDelete = (location: JobRoleLocation) => {
    setLocationToDelete(location);
    setLocationDeleteModalOpen(true);
  };

  const handleDeleteLocation = async () => {
    if (!locationToDelete) return;
    setLocationDeleteLoading(true);
    try {
      await deleteJobRoleLocation(locationToDelete.id);
      setLocationDeleteModalOpen(false);
      setLocationToDelete(null);
    } catch (error) {
      // Error is already toasted
    } finally {
      setLocationDeleteLoading(false);
    }
  };

  // Grade handlers
  const handleAddGrade = async () => {
    if (!newGradeName.trim()) {
      toast.error("Grade name is required");
      return;
    }
    if (newGradeSalaryMin && newGradeSalaryMax && parseFloat(newGradeSalaryMax) < parseFloat(newGradeSalaryMin)) {
      toast.error("Max salary must be greater than or equal to min salary");
      return;
    }
    try {
      setLoading(true);
      await addJobGrade(
        newGradeName.trim(),
        newGradeSalaryMin ? parseFloat(newGradeSalaryMin) : undefined,
        newGradeSalaryMax ? parseFloat(newGradeSalaryMax) : undefined
      );
      setNewGradeName("");
      setNewGradeSalaryMin("");
      setNewGradeSalaryMax("");
    } catch (error) {
      // Error is already toasted
    } finally {
      setLoading(false);
    }
  };

  const startEditGrade = (grade: { id: string; name: string; salaryRangeMin?: number; salaryRangeMax?: number }) => {
    setEditingGrade(grade);
    setEditGradeName(grade.name);
    setEditGradeSalaryMin(grade.salaryRangeMin?.toString() || "");
    setEditGradeSalaryMax(grade.salaryRangeMax?.toString() || "");
  };

  const cancelEditGrade = () => {
    setEditingGrade(null);
    setEditGradeName("");
    setEditGradeSalaryMin("");
    setEditGradeSalaryMax("");
  };

  const handleUpdateGrade = async () => {
    if (!editingGrade) return;
    if (!editGradeName.trim()) {
      toast.error("Grade name is required");
      return;
    }
    if (editGradeSalaryMin && editGradeSalaryMax && parseFloat(editGradeSalaryMax) < parseFloat(editGradeSalaryMin)) {
      toast.error("Max salary must be greater than or equal to min salary");
      return;
    }
    try {
      setLoading(true);
      await updateJobGrade(
        editingGrade.id,
        editGradeName.trim(),
        editGradeSalaryMin ? parseFloat(editGradeSalaryMin) : undefined,
        editGradeSalaryMax ? parseFloat(editGradeSalaryMax) : undefined
      );
      setEditingGrade(null);
      setEditGradeName("");
      setEditGradeSalaryMin("");
      setEditGradeSalaryMax("");
    } catch (error) {
      // Error is already toasted
    } finally {
      setLoading(false);
    }
  };

  const openGradeDelete = (grade: { id: string; name: string }) => {
    setGradeToDelete(grade);
    setGradeDeleteModalOpen(true);
  };

  const handleDeleteGrade = async () => {
    if (!gradeToDelete) return;
    setGradeDeleteLoading(true);
    try {
      await deleteJobGrade(gradeToDelete.id);
      setGradeDeleteModalOpen(false);
      setGradeToDelete(null);
    } catch (error) {
      // Error is already toasted
    } finally {
      setGradeDeleteLoading(false);
    }
  };

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
      {/* Back Button */}
      <button
        onClick={() => navigate("/job-roles")}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors w-fit"
      >
        <ChevronLeft size={18} />
        <span className="text-sm font-medium">Back to Job Roles</span>
      </button>

      {/* Header */}
      <div className="flex items-center gap-3">
        <Settings size={28} className="text-gray-900" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Job Role Settings
          </h1>
          <p className="text-gray-500 text-sm font-medium">
            Manage job titles and other role settings
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-gray-100 p-1 rounded-full w-fit hidden md:flex h-auto mb-2">
          <TabsTrigger
            value="job-titles"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Job Titles
          </TabsTrigger>
          <TabsTrigger
            value="job-locations"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Job Role Locations
          </TabsTrigger>
          <TabsTrigger
            value="job-grades"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Job Role Grades
          </TabsTrigger>
        </TabsList>

        <TabsContent value="job-locations" className="mt-0">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h4 className="font-bold text-gray-900 text-base">
                  Job Role Locations
                </h4>
                <p className="text-gray-500 text-sm font-medium">
                  Manage locations available when creating and editing job positions
                </p>
              </div>
            </div>

            <div className="flex gap-3 items-end">
              <div className="space-y-2 flex-1 max-w-sm">
                <Label htmlFor="newLocation" className="font-bold text-gray-700">
                  Add New Location
                </Label>
                <Input
                  id="newLocation"
                  value={newLocationName}
                  onChange={(e) => setNewLocationName(e.target.value)}
                  placeholder="Enter location name"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddLocation();
                  }}
                />
              </div>
              <Button
                className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-bold shrink-0"
                onClick={handleAddLocation}
                disabled={loading || !newLocationName.trim()}
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                <span>Add Location</span>
              </Button>
            </div>

            {jobRoleLocations.filter(jl => !jl.deleted).length > 0 && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search locations..."
                  value={locationSearchTerm}
                  onChange={(e) => setLocationSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            )}

            <div className="space-y-3">
              {jobRoleLocations
                .filter(jl => !jl.deleted)
                .filter(jl => jl.name.toLowerCase().includes(locationSearchTerm.toLowerCase()))
                .length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-sm font-medium text-gray-500">
                    {locationSearchTerm ? "No locations match your search." : "No locations yet. Add your first location to get started."}
                  </p>
                </div>
              ) : (
                jobRoleLocations
                  .filter(jl => !jl.deleted)
                  .filter(jl => jl.name.toLowerCase().includes(locationSearchTerm.toLowerCase()))
                  .map((location) => (
                    <div
                      key={location.id}
                      className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      {editingLocation?.id === location.id ? (
                        <div className="flex items-center gap-3 flex-1">
                          <Input
                            value={editLocationName}
                            onChange={(e) => setEditLocationName(e.target.value)}
                            className="max-w-sm"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleUpdateLocation();
                              if (e.key === "Escape") cancelEditLocation();
                            }}
                            autoFocus
                          />
                          <Button
                            size="sm"
                            className="bg-slate-950 hover:bg-slate-900 text-white h-9 rounded-xl text-sm font-bold"
                            onClick={handleUpdateLocation}
                            disabled={loading}
                          >
                            {loading ? <Loader2 size={14} className="animate-spin" /> : "Save"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-9 rounded-xl text-sm font-bold"
                            onClick={cancelEditLocation}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm font-bold text-gray-900">
                            {location.name}
                          </p>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              onClick={() => startEditLocation(location)}
                              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => openLocationDelete(location)}
                              className="text-gray-400 hover:text-red-500 transition-colors p-1"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="job-grades" className="mt-0">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h4 className="font-bold text-gray-900 text-base">
                  Job Role Grades
                </h4>
                <p className="text-gray-500 text-sm font-medium">
                  Manage grades available when creating and editing job roles
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="newGrade" className="font-bold text-gray-700">
                  Add New Grade
                </Label>
                <div className="flex gap-3 items-end mt-2">
                  <div className="flex-1 max-w-xs">
                    <Input
                      id="newGrade"
                      value={newGradeName}
                      onChange={(e) => setNewGradeName(e.target.value)}
                      placeholder="Grade name"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddGrade();
                      }}
                    />
                  </div>
                  <div className="flex-1 max-w-[140px]">
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={newGradeSalaryMin}
                      onChange={(e) => setNewGradeSalaryMin(e.target.value)}
                      placeholder="Min $"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddGrade();
                      }}
                    />
                  </div>
                  <div className="flex-1 max-w-[140px]">
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={newGradeSalaryMax}
                      onChange={(e) => setNewGradeSalaryMax(e.target.value)}
                      placeholder="Max $"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddGrade();
                      }}
                    />
                  </div>
                  <Button
                    className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-bold shrink-0"
                    onClick={handleAddGrade}
                    disabled={loading || !newGradeName.trim()}
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                    <span>Add Grade</span>
                  </Button>
                </div>
              </div>
            </div>

            {jobGrades.filter(jg => !jg.deleted).length > 0 && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search grades..."
                  value={gradeSearchTerm}
                  onChange={(e) => setGradeSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            )}

            {/* Column headers */}
            {jobGrades.filter(jg => !jg.deleted).filter(jg => jg.name.toLowerCase().includes(gradeSearchTerm.toLowerCase())).length > 0 && (
              <div className="hidden sm:grid grid-cols-[1fr_140px_140px_80px] gap-4 px-4 pb-2 border-b border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Grade Name</p>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Min Salary</p>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Max Salary</p>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Actions</p>
              </div>
            )}

            <div className="space-y-3">
              {jobGrades
                .filter(jg => !jg.deleted)
                .filter(jg => jg.name.toLowerCase().includes(gradeSearchTerm.toLowerCase()))
                .length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-sm font-medium text-gray-500">
                    {gradeSearchTerm ? "No grades match your search." : "No grades yet. Add your first grade to get started."}
                  </p>
                </div>
              ) : (
                jobGrades
                  .filter(jg => !jg.deleted)
                  .filter(jg => jg.name.toLowerCase().includes(gradeSearchTerm.toLowerCase()))
                  .map((grade) => (
                    <div
                      key={grade.id}
                      className="flex flex-col sm:grid sm:grid-cols-[1fr_140px_140px_80px] gap-2 sm:gap-4 sm:items-center p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      {editingGrade?.id === grade.id ? (
                        <div className="flex items-center gap-3 flex-1 flex-wrap sm:col-span-4">
                          <Input
                            value={editGradeName}
                            onChange={(e) => setEditGradeName(e.target.value)}
                            className="max-w-xs"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleUpdateGrade();
                              if (e.key === "Escape") cancelEditGrade();
                            }}
                            autoFocus
                          />
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            value={editGradeSalaryMin}
                            onChange={(e) => setEditGradeSalaryMin(e.target.value)}
                            placeholder="Min $"
                            className="max-w-[120px]"
                          />
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            value={editGradeSalaryMax}
                            onChange={(e) => setEditGradeSalaryMax(e.target.value)}
                            placeholder="Max $"
                            className="max-w-[120px]"
                          />
                          <Button
                            size="sm"
                            className="bg-slate-950 hover:bg-slate-900 text-white h-9 rounded-xl text-sm font-bold"
                            onClick={handleUpdateGrade}
                            disabled={loading}
                          >
                            {loading ? <Loader2 size={14} className="animate-spin" /> : "Save"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-9 rounded-xl text-sm font-bold"
                            onClick={cancelEditGrade}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between sm:justify-start">
                            <p className="text-sm font-bold text-gray-900">
                              {grade.name}
                            </p>
                            {/* Mobile-only labels */}
                            <div className="sm:hidden flex items-center gap-4 text-xs text-gray-500">
                              <span>Min: {grade.salaryRangeMin !== undefined && grade.salaryRangeMin !== null ? `$${grade.salaryRangeMin}` : '-'}</span>
                              <span>Max: {grade.salaryRangeMax !== undefined && grade.salaryRangeMax !== null ? `$${grade.salaryRangeMax}` : '-'}</span>
                            </div>
                          </div>
                          <p className="hidden sm:block text-sm text-gray-700 text-right">
                            {grade.salaryRangeMin !== undefined && grade.salaryRangeMin !== null ? `$${grade.salaryRangeMin}` : '-'}
                          </p>
                          <p className="hidden sm:block text-sm text-gray-700 text-right">
                            {grade.salaryRangeMax !== undefined && grade.salaryRangeMax !== null ? `$${grade.salaryRangeMax}` : '-'}
                          </p>
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => startEditGrade(grade)}
                              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => openGradeDelete(grade)}
                              className="text-gray-400 hover:text-red-500 transition-colors p-1"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="job-titles" className="mt-0">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            {/* Header Row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h4 className="font-bold text-gray-900 text-base">
                  Job Titles
                </h4>
                <p className="text-gray-500 text-sm font-medium">
                  Manage job titles available when creating and editing job roles
                </p>
              </div>
            </div>

            {/* Add New */}
            {ability.can(ACTIONS.CREATE, RESOURCES.JOB_TITLE) && (
              <div className="flex gap-3 items-end">
                <div className="space-y-2 flex-1 max-w-sm">
                  <Label htmlFor="newTitle" className="font-bold text-gray-700">
                    Add New Job Title
                  </Label>
                  <Input
                    id="newTitle"
                    value={newTitleName}
                    onChange={(e) => setNewTitleName(e.target.value)}
                    placeholder="Enter job title name"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAdd();
                    }}
                  />
                </div>
                <Button
                  className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-bold shrink-0"
                  onClick={handleAdd}
                  disabled={loading || !newTitleName.trim()}
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                  <span>Add Job Title</span>
                </Button>
              </div>
            )}

            {/* Search */}
            {jobTitles.filter(jt => !jt.deleted).length > 0 && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search job titles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            )}

            {/* List */}
            <div className="space-y-3">
              {activeTitles.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-sm font-medium text-gray-500">
                    {searchTerm ? "No job titles match your search." : "No job titles yet. Add your first job title to get started."}
                  </p>
                </div>
              ) : (
                activeTitles.map((title) => (
                  <div
                    key={title.id}
                    className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    {editingTitle?.id === title.id ? (
                      <div className="flex items-center gap-3 flex-1">
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="max-w-sm"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleUpdate();
                            if (e.key === "Escape") cancelEdit();
                          }}
                          autoFocus
                        />
                        <Button
                          size="sm"
                          className="bg-slate-950 hover:bg-slate-900 text-white h-9 rounded-xl text-sm font-bold"
                          onClick={handleUpdate}
                          disabled={loading}
                        >
                          {loading ? <Loader2 size={14} className="animate-spin" /> : "Save"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-9 rounded-xl text-sm font-bold"
                          onClick={cancelEdit}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm font-bold text-gray-900">
                          {title.name}
                        </p>
                        <div className="flex items-center gap-2 shrink-0">
                          {ability.can(ACTIONS.UPDATE, RESOURCES.JOB_TITLE) && (
                            <button
                              onClick={() => startEdit(title)}
                              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            >
                              <Pencil size={16} />
                            </button>
                          )}
                          {ability.can(ACTIONS.DELETE, RESOURCES.JOB_TITLE) && (
                            <button
                              onClick={() => openDeleteFlow(title)}
                              className="text-gray-400 hover:text-red-500 transition-colors p-1"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Warning Modal — Title in use by job roles */}
      <AlertDialog open={warnModalOpen} onOpenChange={setWarnModalOpen}>
        <AlertDialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <AlertDialogHeader className="p-6 pb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-amber-500" />
              <AlertDialogTitle className="text-lg font-bold text-gray-900">
                Job Title in Use
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
              <span className="font-bold text-gray-700">"{titleToDelete?.name}"</span> is currently used by the following active job roles. Deleting it will mark it as <span className="font-bold text-gray-700">(deleted)</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="px-6 pb-2">
            <div className="bg-gray-50 rounded-xl border border-gray-100 max-h-48 overflow-y-auto">
              {affectedRoles.map((role) => (
                <div
                  key={role.id}
                  className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0"
                >
                  <Briefcase size={16} className="text-gray-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">{role.title}</p>
                    <p className="text-xs text-gray-500 font-medium flex items-center gap-1">
                      <Building2 size={12} />
                      {role.department}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <AlertDialogFooter className="px-6 pb-6 gap-2 pt-4">
            <AlertDialogCancel
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
              onClick={() => { setTitleToDelete(null); setAffectedRoles([]); }}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5"
              onClick={proceedToDelete}
            >
              Continue to Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Modal */}
      <AlertDialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <AlertDialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <AlertDialogHeader className="p-6 pb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-red-500" />
              <AlertDialogTitle className="text-lg font-bold text-gray-900">Delete Job Title</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
              Are you sure you want to delete <span className="font-bold text-gray-700">"{titleToDelete?.name}"</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="px-6 pb-6 gap-2">
            <AlertDialogCancel
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
              disabled={deleteLoading}
              onClick={() => { setTitleToDelete(null); setAffectedRoles([]); }}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5"
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                "Delete Job Title"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Location Delete Confirmation Modal */}
      <AlertDialog open={locationDeleteModalOpen} onOpenChange={setLocationDeleteModalOpen}>
        <AlertDialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <AlertDialogHeader className="p-6 pb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-red-500" />
              <AlertDialogTitle className="text-lg font-bold text-gray-900">Delete Location</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
              Are you sure you want to delete <span className="font-bold text-gray-700">"{locationToDelete?.name}"</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="px-6 pb-6 gap-2">
            <AlertDialogCancel
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
              disabled={locationDeleteLoading}
              onClick={() => setLocationToDelete(null)}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5"
              onClick={handleDeleteLocation}
              disabled={locationDeleteLoading}
            >
              {locationDeleteLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                "Delete Location"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Grade Delete Confirmation Modal */}
      <AlertDialog open={gradeDeleteModalOpen} onOpenChange={setGradeDeleteModalOpen}>
        <AlertDialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <AlertDialogHeader className="p-6 pb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-red-500" />
              <AlertDialogTitle className="text-lg font-bold text-gray-900">Delete Job Grade</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
              Are you sure you want to delete <span className="font-bold text-gray-700">"{gradeToDelete?.name}"</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="px-6 pb-6 gap-2">
            <AlertDialogCancel
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
              disabled={gradeDeleteLoading}
              onClick={() => setGradeToDelete(null)}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5"
              onClick={handleDeleteGrade}
              disabled={gradeDeleteLoading}
            >
              {gradeDeleteLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                "Delete Grade"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default JobRoleSettings;
